import { useMemo } from 'react';

import type {
  MetricEvent,
  MetricEventName,
} from '@spotify-internal/csr-player';
import { ColorUtils } from '@spotify/confidence-core-react';

export type EventCategory = {
  id: string;
  label: string;
  color: string;
};

const DEFAULT_COLOR = 'var(--text-subdued)';

const EVENT_LABELS: Record<MetricEventName, string> = {
  click: 'Clicks',
  input: 'Inputs',
  'rage-click': 'Rage clicks',
  'dead-click': 'Dead clicks',
  'form-field-re-edit': 'Re-edits',
  'scroll-back': 'Scroll backs',
  'tab-unfocus': 'Tab unfocuses',
  'tab-refocus': 'Tab refocuses',
  'route-change': 'Route changes',
};

type EventCategoriesResult = {
  categories: EventCategory[];
  getEventColor: (eventName: string) => string;
};

export const useEventCategories = (
  events: MetricEvent[],
): EventCategoriesResult => {
  return useMemo(() => {
    const colorMap = new Map<string, string>();
    const categories: EventCategory[] = [];

    for (const event of events) {
      if (colorMap.has(event.name)) continue;
      const color =
        ColorUtils.colorList[categories.length % ColorUtils.colorList.length];
      colorMap.set(event.name, color);
      categories.push({
        id: event.name,
        label: EVENT_LABELS[event.name] ?? event.displayName,
        color,
      });
    }

    const getEventColor = (eventName: string): string =>
      colorMap.get(eventName) ?? DEFAULT_COLOR;

    return { categories, getEventColor };
  }, [events]);
};
